import type { DialogPassThroughOptions } from 'primevue/dialog';
import type { DynamicDialogInstance, DynamicDialogOptions } from 'primevue/dynamicdialogoptions';
import type {
    ModalDataForComponent,
    ModalDataMap,
    ModalWithOptionalData,
    ModalWithRequiredData,
    ModalWithoutData,
    PbDialogProps,
    TypedDialogInstance,
} from '~/types/modals';
import { ModalComponents, ModalNames } from '~/types/modals';

// EXPOSURE_SOURCES -> "Exposure sources", so a caller that passes no label still opens a named dialog.
function defaultDialogLabel(name: ModalNames): string {
    const key = ModalNames[name];

    return key.charAt(0) + key.slice(1).toLowerCase().replace(/_/g, ' ');
}

// Modals that render their own edge-anchored drawer: their root element is
// `fixed`, so it contributes no height to the dialog box around it. The dialog
// preset paints that box as a card — white, shadowed, `px-6 py-4` — and the
// `.modal-main > div` rule stretches it to `sm:w-screen`, so what is left of it
// renders as a full-width white bar across the page behind the drawer. Zeroing
// the chrome collapses the box to nothing, which is what the two billing
// drawers were already doing at their call sites.
const DRAWER_MODALS = new Set<ModalNames>([
    ModalNames.EXPOSURE_SIDEBAR,
    ModalNames.EMPLOYEE_DETAILS_DRAWER,
    ModalNames.LICENSE_BLOCK_DRAWER,
    ModalNames.CONSOLIDATE_BILLING_DRAWER,
]);

const DRAWER_PT = {
    root: 'p-0 m-0',
    content: 'p-0',
} as const;

// Required/optional data enforcement is derived from ModalDataMap (see types/modals.ts) —
// a modal's payload type is the single source of truth for what its callers must pass.
export function useDynamicDialog<T extends ModalWithRequiredData>(name: T, data: ModalDataMap[T], props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog<T extends ModalWithOptionalData>(name: T, data?: ModalDataMap[T], props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog<T extends ModalWithoutData>(name: T, data?: undefined, props?: PbDialogProps): DynamicDialogInstance;
export function useDynamicDialog(name: ModalNames, data?: unknown, props?: PbDialogProps): DynamicDialogInstance {
    const nuxtApp = useNuxtApp();

    const defaultProps: PbDialogProps = {
        modal: true,
        closable: false,
        dismissableMask: false,
        // Escape closes a PrimeVue Dialog through its own document key handler,
        // which ignores `closable` and never reaches the modal's close handler
        // — so it dismisses mid-request and hands the parent's onClose no
        // result payload, skipping the refresh. Every modal renders its own
        // guarded close control; that is the only way out.
        closeOnEscape: false,
        // Every modal draws its own heading and close control. PrimeVue renders its header
        // regardless, and in a box stretched to `sm:w-screen` that empty strip's bottom border is
        // a hairline across the whole page, 16px above the modal.
        showHeader: false,
    };

    const { label, pt: callerPt, ...callerProps } = props ?? {};
    const rawRoot = (callerPt as DialogPassThroughOptions | undefined)?.root;
    // `root` is replaced below to carry the aria attributes, so a caller's shorthand class string
    // has to be folded into that object — otherwise the dialog loses its width and its
    // `.modal-main > div` preset stretches it to `sm:w-screen`.
    const callerRoot = typeof rawRoot === 'string'
        ? { class: rawRoot }
        : rawRoot !== null && typeof rawRoot === 'object' ? rawRoot : undefined;

    const isDrawer = DRAWER_MODALS.has(name);
    // The drawer default has to join the same object, since `root` is rebuilt
    // below rather than spread from `callerPt`. A caller that names its own root
    // still wins.
    const rootDefaults = isDrawer && callerRoot?.class === undefined ? { class: DRAWER_PT.root } : {};

    const dialogOptions: DynamicDialogOptions = {
        props: {
            ...defaultProps,
            ...callerProps,
            pt: {
                mask: 'bg-[#09090b80] modal-main',
                ...(isDrawer ? { content: DRAWER_PT.content } : {}),
                ...callerPt,
                root: {
                    // PrimeVue always points aria-labelledby at a title span it only renders when
                    // `header` is set, so every dialog carries a dangling reference. A header would
                    // add a visible bar to all modals, so the name is supplied as aria-label and the
                    // dangling reference is cleared. ptmi('root') is merged last, so pt wins here.
                    'aria-labelledby': null,
                    'aria-label': label ?? defaultDialogLabel(name),
                    ...rootDefaults,
                    ...(callerRoot ?? {}),
                },
            },
        },
        onClose: props?.onClose,
    };

    if (data !== undefined) {
        dialogOptions.data = data;
    }

    return nuxtApp.$dialog.open(ModalComponents[name as keyof typeof ModalComponents](), dialogOptions);
}

export function getDialogRef<T extends keyof typeof ModalComponents>(_name: T): Ref<TypedDialogInstance<ModalDataForComponent<T>>> {
    return inject<Ref<TypedDialogInstance<ModalDataForComponent<T>>>>('dialogRef')!;
}
